import { useKeycloakToken } from '../common/useKeycloak';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConnectedSystemsList } from '../../api/destinations/destinationsApi';
import { IntegrationRoles, loadIntegrationRoleMap } from '../../api/connections/connectionsApi';
import DestinationsList from './DestinationsList';
import { Destination, DestinationType, Organization } from '../common/ObjectTypes';
import { ManageDestinationDetails } from './ManageDestinationDetails';
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from '../../api/apiUtils';
import loadDestinationUrls from '../../api/destinations/loadDestinationUrls';
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import getUserPermissions from '../../api/users/getUserPermissions';
import { useKeycloak } from '@react-keycloak/web';
import { useHelp } from '../../context/HelpContext';
import { useTranslation } from 'react-i18next';

const DestinationsPage = () => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();

    useEffect(() => {
        setHelpContent({
            cards: [{
                title: t('Managing Connected Systems in Gundi'),
                description: t('Learn how to set up the systems Gundi exchanges data with, as sources or as destinations.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/destinations' },
            }],
        });
    }, [i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);
    type FilterTypes = {
        base_url__in: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    };
    type FilterTags = {
        name: string,
        id: string
    };
    const defaultDestinationsFilter = {
        base_url__in: [],
        type__in: [],
        owner__in: [],
        ordering: "name",
        search: ""
    };
    const navigate = useNavigate();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak } = useKeycloak();
    const [canAddDestination, setCanAddDestination] = useState(false);
    const [destinations, setDestinations] = useState<Destination[]>([]);
    // Which connections use each record, and in which role. No endpoint reports this
    // per integration, so it is derived once from the connections list and reused for
    // every row. Refreshed with the list.
    const [roleMap, setRoleMap] = useState<Record<string, IntegrationRoles>>({});
    // Until the sweep lands, a record's roles are unknown — which is not the same as
    // "not in use", so the chips wait rather than claiming something false.
    const [roleMapLoaded, setRoleMapLoaded] = useState(false);
    const [roleRefreshToken, setRoleRefreshToken] = useState(0);
    const [roleFilter, setRoleFilter] = useState<{ label: string, value: string } | null>(null);
    // The ids a role resolves to, from the connections sweep. null = no role filter.
    const roleFilterIds: string[] | null = !roleFilter ? null : Object.keys(roleMap).filter((id) => {
        const roles = roleMap[id];
        return roleFilter.value === "source" ? roles.asProvider.length > 0 : roles.asDestination.length > 0;
    });
    const [destinationFilter, setDestinationFilter] = useState<FilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('destinationFilter');
        return savedFilter ? { ...defaultDestinationsFilter, ...JSON.parse(savedFilter) } : defaultDestinationsFilter;
    });
    const [totalCount, setTotalCount] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);
    const [destinationUrls, setDestinationUrls] = useState<any[]>([]);
    const [loadingDestinationUrls, setLoadingDestinationUrls] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [reloadDestinations, setReloadDestinations] = useState(true);
    const [destDetailsModal, setDestDetailsModal] = useState(false);

    const loadDestinationURlsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationUrls(true);
            try {
                let accumulated: string[] = [];
                let urlsOffset = 0;
                while (true) {
                    const [dest_urls, total, error] = await loadDestinationUrls(token, urlsOffset, "", accumulated);
                    if (error) break;
                    accumulated = dest_urls;
                    if (urlsOffset + filter_page_size >= total) break;
                    urlsOffset += filter_page_size;
                }
                setDestinationUrls([...new Set(accumulated)]);
            } finally {
                setLoadingDestinationUrls(false);
            }
        }
    }

    const loadDestinationTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationTypes(true);
            try {
                let accumulated: DestinationType[] = [];
                let typesOffset = 0;
                while (true) {
                    const [types, total, error] = await loadDestinationTypes(token, typesOffset, "", accumulated, true);
                    if (error) break;
                    accumulated = types;
                    if (typesOffset + filter_page_size >= total) break;
                    typesOffset += filter_page_size;
                }
                setDestinationTypes(accumulated);
            } finally {
                setLoadingDestinationTypes(false);
            }
        }
    }

    const loadOrganizationsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingOrganizations(true);
            try {
                let accumulated: Organization[] = [];
                let orgsOffset = 0;
                while (true) {
                    const [orgs, total, error] = await loadOrganizations(token, orgsOffset, "", accumulated);
                    if (error) break;
                    accumulated = orgs;
                    if (orgsOffset + filter_page_size >= total) break;
                    orgsOffset += filter_page_size;
                }
                setOrganizations(accumulated);
            } finally {
                setLoadingOrganizations(false);
            }
        }
    }

    useEffect(() => {
        sessionStorage.setItem('destinationFilter', JSON.stringify(destinationFilter));

        const loadPerms = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const temp = await getUserPermissions(token);
                    setCanAddDestination(temp);
                } catch (err) {
                    console.log(err);
                }
            }
        }

        const loadDestinations = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const destinationFilterQuery = {
                        base_url__in: destinationFilter.base_url__in.map(a => a.id).toString(),
                        type__in: destinationFilter.type__in.map(a => a.id).toString(),
                        owner__in: destinationFilter.owner__in.map(a => a.id).toString(),
                        ordering: destinationFilter.ordering,
                        search: destinationFilter.search,
                        offset: offset,
                        previous: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (offset - page_size),
                        next: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (offset + page_size)
                    }
                    // Role is not a field the API filters on, but it resolves to a set of
                    // ids the connections sweep already gave us — pushed down as id__in so
                    // the server pages the real result set instead of the browser hiding
                    // rows from one page. No ids means no matches, so don't ask.
                    if (roleFilterIds !== null && roleFilterIds.length === 0) {
                        setDestinations([]);
                        setTotalCount(0);
                        setPageLoaded(true);
                        return;
                    }
                    const temp = await getConnectedSystemsList(token, roleFilterIds === null
                        ? destinationFilterQuery
                        : { ...destinationFilterQuery, id__in: roleFilterIds.join(",") });
                    setDestinations(temp.results);
                    setTotalCount(temp.count);
                    setPageLoaded(true);
                } catch (err) {
                    console.log(err);
                } finally {
                    // Always settle the flag — a failed request must not leave
                    // the refresh button disabled and spinning forever.
                    setReloadDestinations(false);
                }
            }
        }

        if (reloadDestinations) loadDestinations();
        if (!pageLoaded) {
            loadPerms();
            loadOrganizationsData();
            loadDestinationURlsData();
            loadDestinationTypesData();
        }
    }, [token, reloadDestinations, destinationFilter]);

    // Roles come from every connection, not from the list's filters, so the sweep
    // runs once per visit (and on an explicit refresh) instead of on every search
    // keystroke or page change. The ref drops a stale response that lands late.
    const roleSweepId = useRef(0);
    useEffect(() => {
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        const sweep = ++roleSweepId.current;
        loadIntegrationRoleMap(token)
            .then((map) => {
                if (sweep !== roleSweepId.current) return;
                setRoleMap(map);
                setRoleMapLoaded(true);
            })
            .catch((err) => console.log(err));
        return () => { roleSweepId.current += 1; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, roleRefreshToken]);

    const handleAddDestinationPage = () => {
        navigate('/connected-systems/new');
    }

    const handleToggleDestinationDetailsModal = async (dest_id?: string) => {
        if (dest_id && token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            navigate(`/connected-systems/${dest_id}/configuration`, { replace: false });
        }
        setDestDetailsModal(!destDetailsModal);
        if (destDetailsModal) setReloadDestinations(true);
    }

    const handleGlobalSearch = (searchSubStr: string) => {
        setDestinationFilter(prev => ({ ...prev, search: searchSubStr }));
        setOffset(0);
        setReloadDestinations(true);
    }

    const handleFilterEndpoint = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, base_url__in: tags }));
        setOffset(0);
        setReloadDestinations(true);
    }

    const handleFilterType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, type__in: tags }));
        setOffset(0);
        setReloadDestinations(true);
    }

    const handleFilterOwner = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, owner__in: tags }));
        setOffset(0);
        setReloadDestinations(true);
    }

    const handleSortBy = (selected: { label: string, value: string }) => {
        setDestinationFilter(prev => ({ ...prev, ordering: selected.value }));
        setOffset(0);
        setReloadDestinations(true);
    }

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
        setReloadDestinations(true);
    }

    const toSingleValue = (tags: FilterTags[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const endpointSelectValue = toSingleValue(destinationFilter.base_url__in);
    const typeSelectValue = toSingleValue(destinationFilter.type__in);
    const ownerSelectValue = toSingleValue(destinationFilter.owner__in);

    return (
            <div>
                {destDetailsModal &&
                    <ManageDestinationDetails />
                }
                <DestinationsList
                    canAddDestination={canAddDestination}
                    reloadDestinations={reloadDestinations}
                    destinations={destinations}
                    roleFilterValue={roleFilter}
                    onChangeRole={(selected) => {
                        setRoleFilter(selected);
                        setOffset(0);
                        setReloadDestinations(true);
                    }}
                    roleFilterDisabled={!roleMapLoaded}
                    destinationTypes={destinationTypes}
                    loadingDestinationTypes={loadingDestinationTypes}
                    destinationUrls={destinationUrls}
                    loadingDestinationUrls={loadingDestinationUrls}
                    organizations={organizations}
                    loadingOrganizations={loadingOrganizations}
                    onAddDestination={handleAddDestinationPage}
                    onViewDetails={handleToggleDestinationDetailsModal}
                    onChangeGlobalSearch={handleGlobalSearch}
                    onRefresh={() => { setReloadDestinations(true); setRoleRefreshToken((n) => n + 1); }}
                    globalSearchStr={destinationFilter.search}
                    ordering={destinationFilter.ordering}
                    onChangeSortBy={handleSortBy}
                    onChangeEndpoint={handleFilterEndpoint}
                    endpointFilterValue={endpointSelectValue}
                    onChangeType={handleFilterType}
                    typeFilterValue={typeSelectValue}
                    onChangeOwner={handleFilterOwner}
                    ownerFilterValue={ownerSelectValue}
                    onPageClicked={handlePageClick}
                    totalCount={totalCount}
                    pageLoaded={pageLoaded}
                    take={page_size}
                    offset={offset}
                />
            </div>
        );
};

export default DestinationsPage;
